import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Plus, PencilSimple, Trash, Eye, EyeSlash, ArrowSquareOut, FloppyDisk, X, Lock,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  marche: 'Marché',
  assistance: 'Assistance',
  legal: 'Légal',
  system: 'Système',
  other: 'Autres',
};

const slugify = (s) => (s || '')
  .toLowerCase()
  .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^\w\s-]/g, '')
  .replace(/[\s_-]+/g, '-')
  .replace(/^-+|-+$/g, '');

const emptyPage = {
  page_id: null,
  slug: '',
  title: { fr: '', en: '', mg: '' },
  content: { fr: '', en: '', mg: '' },
  category: 'other',
  is_published: true,
  is_default: false,
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote'],
    ['clean'],
  ],
};

export const PagesManagement = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contentLang, setContentLang] = useState('fr');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pages`, { withCredentials: true });
      setPages(data.pages || []);
    } catch { toast.error('Failed to load pages'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing({ ...emptyPage }); setContentLang('fr'); };
  const openEdit = (p) => { setEditing({ ...p }); setContentLang('fr'); };

  const save = async () => {
    if (!editing) return;
    if (!editing.title.fr?.trim()) {
      toast.error('French title is required'); return;
    }
    setSaving(true);
    const slug = editing.slug?.trim() || slugify(editing.title.fr);
    const payload = {
      title: editing.title,
      slug,
      content: editing.content,
      category: editing.category,
      is_published: editing.is_published,
    };
    try {
      if (editing.page_id) {
        await axios.put(`${API}/admin/pages/${editing.page_id}`, payload, { withCredentials: true });
        toast.success('Page updated');
      } else {
        await axios.post(`${API}/admin/pages`, payload, { withCredentials: true });
        toast.success('Page created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const togglePublish = async (p) => {
    try {
      await axios.patch(`${API}/admin/pages/${p.page_id}/toggle`, {}, { withCredentials: true });
      load();
    } catch { toast.error('Toggle failed'); }
  };

  const del = async (p) => {
    if (p.is_default) {
      toast.error('Default pages cannot be deleted — unpublish instead');
      return;
    }
    if (!window.confirm(`Delete page "${p.title?.fr}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pages/${p.page_id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const resetToDefault = async (p) => {
    if (!window.confirm(`Restore the default content for "${p.title?.fr}"? Your current edits will be lost.`)) return;
    try {
      await axios.post(`${API}/admin/pages/${p.page_id}/reset-default`, {}, { withCredentials: true });
      toast.success('Page content restored to default');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  // Auto-fill slug while typing title (only for NEW pages, never when editing defaults)
  const onTitleChange = (lang, value) => {
    const newTitle = { ...editing.title, [lang]: value };
    const autoSlug = !editing.page_id && lang === 'fr' ? slugify(value) : editing.slug;
    setEditing({ ...editing, title: newTitle, slug: autoSlug });
  };

  const defaultPages = pages.filter(p => p.is_default);
  const customPages = pages.filter(p => !p.is_default);

  const renderRow = (p) => (
    <div key={p.page_id}
      className="flex items-center gap-3 border border-border rounded-md p-3 bg-white"
      data-testid={`page-row-${p.slug}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{p.title?.fr}</span>
          <Badge variant="outline" className="text-xs">{CATEGORY_LABELS[p.category] || p.category}</Badge>
          {p.is_default && <Badge className="bg-indigo-600 text-xs gap-1"><Lock size={10} /> Default</Badge>}
          {!p.is_published && <Badge variant="destructive" className="text-xs">Draft</Badge>}
        </div>
        <p className="text-xs text-text_muted mt-0.5">
          <code className="bg-surface px-1.5 py-0.5 rounded">/pages/{p.slug}</code>
        </p>
      </div>
      <a href={`/pages/${p.slug}`} target="_blank" rel="noreferrer"
        className="text-text_muted hover:text-primary" title="Open page" data-testid={`open-${p.slug}`}>
        <ArrowSquareOut size={18} />
      </a>
      <button onClick={() => togglePublish(p)}
        className={`p-1 ${p.is_published ? 'text-success' : 'text-text_muted'}`}
        title={p.is_published ? 'Published — click to unpublish' : 'Draft — click to publish'}
        data-testid={`toggle-${p.slug}`}>
        {p.is_published ? <Eye size={22} weight="fill" /> : <EyeSlash size={22} />}
      </button>
      <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`}>
        <PencilSimple size={14} />
      </Button>
      {p.is_default && (
        <Button
          variant="outline" size="sm"
          onClick={() => resetToDefault(p)}
          title="Reset to default content"
          className="text-text_muted hover:text-primary"
          data-testid={`reset-default-${p.slug}`}
        >
          <ArrowCounterClockwise size={14} />
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={() => del(p)}
        disabled={p.is_default}
        className={p.is_default ? 'opacity-40 cursor-not-allowed' : 'text-danger hover:bg-danger-soft'}
        data-testid={`delete-${p.slug}`}>
        <Trash size={14} />
      </Button>
    </div>
  );

  return (
    <div data-testid="pages-management">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Pages</h2>
          <p className="text-sm text-text_muted">Manage default footer pages and create unlimited custom pages at <code>/pages/{'{slug}'}</code>.</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-white" data-testid="create-page-btn">
          <Plus size={16} className="mr-1.5" weight="bold" /> Create New Page
        </Button>
      </div>

      {loading ? <p className="text-text_muted">Loading…</p> : (
        <div className="space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-text_muted uppercase tracking-wider mb-3">Default Pages ({defaultPages.length})</h3>
            <div className="space-y-2">{defaultPages.map(renderRow)}</div>
          </section>
          <section>
            <h3 className="text-sm font-semibold text-text_muted uppercase tracking-wider mb-3">Custom Pages ({customPages.length})</h3>
            {customPages.length === 0 ? (
              <div className="bg-white border border-dashed border-border rounded-md p-6 text-center">
                <p className="text-sm text-text_muted">No custom pages yet. Click "Create New Page" to add one.</p>
              </div>
            ) : <div className="space-y-2">{customPages.map(renderRow)}</div>}
          </section>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="page-editor">
          <DialogHeader>
            <DialogTitle>
              {editing?.page_id ? `Edit: ${editing.title?.fr}` : 'Create New Page'}
              {editing?.is_default && <Badge className="ml-2 bg-indigo-600 text-xs gap-1 align-middle"><Lock size={10} /> Default — slug locked</Badge>}
            </DialogTitle>
            <DialogDescription>
              Content supports basic rich text (titles, bold, lists, links). Use the language tabs to manage FR/EN/MG versions.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              {/* Title — 3 languages */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Title FR *</Label>
                  <Input value={editing.title.fr} onChange={e => onTitleChange('fr', e.target.value)} data-testid="page-title-fr" />
                </div>
                <div>
                  <Label>Title EN</Label>
                  <Input value={editing.title.en || ''} onChange={e => onTitleChange('en', e.target.value)} data-testid="page-title-en" />
                </div>
                <div>
                  <Label>Title MG</Label>
                  <Input value={editing.title.mg || ''} onChange={e => onTitleChange('mg', e.target.value)} data-testid="page-title-mg" />
                </div>
              </div>

              {/* Slug + Category + Publish */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Slug * <span className="text-xs text-text_muted">(URL-friendly)</span></Label>
                  <Input
                    value={editing.slug}
                    onChange={e => setEditing({ ...editing, slug: slugify(e.target.value) })}
                    disabled={editing.is_default}
                    placeholder="my-page"
                    data-testid="page-slug"
                  />
                  <p className="text-[10px] text-text_muted mt-1 font-mono">
                    URL: /pages/{editing.slug || '...'}
                  </p>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="page-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <input type="checkbox" id="page-pub" checked={editing.is_published}
                    onChange={e => setEditing({ ...editing, is_published: e.target.checked })}
                    data-testid="page-published" />
                  <Label htmlFor="page-pub" className="cursor-pointer">Published</Label>
                </div>
              </div>

              {/* Content tabs */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Label>Content</Label>
                  <div className="ml-auto flex gap-1">
                    {['fr', 'en', 'mg'].map(lg => (
                      <button
                        key={lg}
                        onClick={() => setContentLang(lg)}
                        className={`text-xs px-3 py-1 rounded ${contentLang === lg ? 'bg-primary text-white' : 'bg-surface text-text_muted hover:text-primary'}`}
                        data-testid={`content-tab-${lg}`}
                      >
                        {lg.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border border-border rounded bg-white" data-testid={`content-editor-${contentLang}`}>
                  <ReactQuill
                    theme="snow"
                    value={editing.content?.[contentLang] || ''}
                    onChange={(html) => setEditing({ ...editing, content: { ...editing.content, [contentLang]: html } })}
                    modules={QUILL_MODULES}
                    style={{ minHeight: 260 }}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="page-cancel-btn">
              <X size={14} className="mr-1" /> Cancel
            </Button>
            {editing?.page_id && (
              <Button variant="outline" onClick={() => window.open(`/pages/${editing.slug}`, '_blank')} data-testid="page-preview-btn">
                <ArrowSquareOut size={14} className="mr-1.5" /> Preview
              </Button>
            )}
            <Button onClick={save} disabled={saving} className="bg-primary text-white" data-testid="page-save-btn">
              <FloppyDisk size={14} className="mr-1.5" weight="bold" />
              {saving ? 'Saving…' : editing?.page_id ? 'Update Page' : 'Create Page'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
